import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MapPin, Briefcase, Star, Eye, Shield, ExternalLink } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

const parseCultural = (meta) => {
  if (!meta) return {};
  if (typeof meta === 'string') { try { return JSON.parse(meta); } catch { return {}; } }
  return meta;
};

export default function CandidatePortal() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setError('Missing access token. Please use the link provided to you.'); setLoading(false); return; }
    fetch(`${BACKEND}/api/candidates/portal/${id}?token=${encodeURIComponent(token)}`)
      .then(async r => {
        const d = await r.json();
        if (r.ok && d.success) setCandidate(d.data);
        else setError(d.detail || 'Invalid or expired link.');
        setLoading(false);
      })
      .catch(() => { setError('Could not load profile.'); setLoading(false); });
  }, [id, token]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <p style={MONO} className="text-xs text-muted-foreground uppercase tracking-widest">Loading your profile...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
      <Shield className="w-8 h-8 text-muted-foreground" />
      <p style={SERIF} className="text-xl font-light text-center">
        {error}
      </p>
      <p className="text-sm text-muted-foreground text-center max-w-sm leading-relaxed">
        This preview link may have expired or is invalid.
        Profile links are automatically refreshed each time Flence syncs your profile to FashionSpyder.
        To get a fresh link, ask your Flence account manager to trigger a profile re-sync.
      </p>
    </div>
  );

  const cultural = parseCultural(candidate.cultural_metadata);
  const skills = [
    ...(candidate.software_skills || []),
    ...(candidate.manual_creative_skills || []),
  ].slice(0, 16);

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      {/* Top notice bar */}
      <div className="bg-foreground text-background text-center py-2.5 px-4">
        <p style={MONO} className="text-[10px] uppercase tracking-widest">
          Your FashionSpyder profile — read only · To update, use your Flence recruitment panel
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">

        {/* Header */}
        <div className="flex items-start gap-6">
          {candidate.photo_url ? (
            <img src={candidate.photo_url} alt={candidate.full_name}
              className="w-20 h-20 object-cover flex-shrink-0 border border-border" />
          ) : (
            <div className="w-20 h-20 bg-secondary flex items-center justify-center text-2xl font-light flex-shrink-0 border border-border">
              {(candidate.full_name || '?')[0]}
            </div>
          )}
          <div className="flex-1">
            <h1 style={SERIF} className="text-3xl font-light">{candidate.full_name}</h1>
            <p style={MONO} className="text-xs text-[hsl(var(--primary))] uppercase tracking-widest mt-1">
              {candidate.core_role} · {candidate.seniority}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              {candidate.location_city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {candidate.location_city}{candidate.location_country ? `, ${candidate.location_country}` : ''}
                </span>
              )}
              {candidate.availability && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {candidate.availability}
                </span>
              )}
              {candidate.overall_ranking > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current text-[hsl(var(--primary))]" />
                  {candidate.overall_ranking?.toFixed(1)} Visibility Index
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Privacy notice */}
        <div className="border border-border border-l-4 border-l-[hsl(var(--primary))] bg-white px-5 py-3">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold">Your contact details are protected</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                HR recruiters cannot see your email, phone, or social profiles. All communication happens through our secure messaging system.
              </p>
            </div>
          </div>
        </div>

        {/* Bio */}
        {candidate.optimized_bio && (
          <section className="bg-white border border-border p-6">
            <p style={MONO} className="text-[9px] uppercase tracking-widest text-muted-foreground mb-3">Profile Summary</p>
            <p className="text-sm leading-relaxed">{candidate.optimized_bio}</p>
          </section>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <section className="bg-white border border-border p-6">
            <p style={MONO} className="text-[9px] uppercase tracking-widest text-muted-foreground mb-3">Skills</p>
            <div className="flex flex-wrap gap-2">
              {skills.map((s, i) => (
                <span key={i} className="text-xs px-2.5 py-1 bg-secondary border border-border">
                  {typeof s === 'string' ? s : s.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Style DNA */}
        {candidate.ai_style_dna?.length > 0 && (
          <section className="bg-white border border-border p-6">
            <p style={MONO} className="text-[9px] uppercase tracking-widest text-muted-foreground mb-3">Style DNA</p>
            <div className="flex flex-wrap gap-2">
              {candidate.ai_style_dna.map((tag, i) => (
                <span key={i} className="text-xs px-2.5 py-1 bg-foreground text-background">{tag}</span>
              ))}
            </div>
          </section>
        )}

        {/* Cultural tags */}
        {cultural.historian_tags?.length > 0 && (
          <section className="bg-white border border-border p-6">
            <p style={MONO} className="text-[9px] uppercase tracking-widest text-muted-foreground mb-3">Aesthetic Signature</p>
            <div className="flex flex-wrap gap-2">
              {cultural.historian_tags.map((tag, i) => (
                <span key={i} className="text-xs px-2.5 py-1 border border-border text-muted-foreground">{tag}</span>
              ))}
            </div>
            {cultural.aesthetic_description && (
              <p className="text-xs text-muted-foreground mt-3 italic">{cultural.aesthetic_description}</p>
            )}
          </section>
        )}

        {/* Work preferences */}
        <section className="bg-white border border-border p-6">
          <p style={MONO} className="text-[9px] uppercase tracking-widest text-muted-foreground mb-3">Work Preferences</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {candidate.work_preferences?.length > 0 && (
              <div>
                <span className="text-muted-foreground">Mode: </span>
                {candidate.work_preferences.join(', ')}
              </div>
            )}
            {candidate.languages?.length > 0 && (
              <div>
                <span className="text-muted-foreground">Languages: </span>
                {candidate.languages.map(l => typeof l === 'string' ? l : l.language).join(', ')}
              </div>
            )}
            {candidate.years_experience > 0 && (
              <div>
                <span className="text-muted-foreground">Experience: </span>
                {candidate.years_experience} years
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <p style={MONO} className="text-[9px] uppercase tracking-widest text-muted-foreground">
            Powered by FashionSpyder · Talent Intelligence Platform
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Want to update your profile? Log in to your{' '}
            <a href="https://flence.com" target="_blank" rel="noreferrer"
              className="underline hover:text-foreground">Flence account</a>.
          </p>
        </div>

      </div>
    </div>
  );
}
